import { ofetch, type FetchContext, type FetchOptions } from 'ofetch'

enum HttpMethod {
    'GET' = 'GET',
    'POST' = 'POST',
    'PUT' = 'PUT',
    'PATCH' = 'PATCH',
    'DELETE' = 'DELETE'
}

const createUrlAppend = (options: { url?: string; fetchParams?: FetchParams }): string => {
    return `${options.url ?? ''}${isEmpty(options.fetchParams) ? '' : `?${useParamTransformer(options.fetchParams ?? {})}`}`
}

export const useFetch = (options: FetchOptions = {}) => {
    const ofetchInstance = ofetch.create(options)

    return {
        get: <T>(options: {
            url?: string
            fetchParams?: FetchParams
            signal?: AbortSignal
            isCsv?: boolean
        }) => {
            const { isCsv = false } = options
            return ofetchInstance<T>(createUrlAppend(options), {
                method: HttpMethod.GET,
                signal: options.signal,
                onRequest: ({ options }: FetchContext) => {
                    options.headers = {
                        ...contentTypeHeaders(isCsv),
                        ...options.headers,
                        ...authHeader(),
                        ...switchHeader()
                    }
                }
            })
        },
        post: <T>(options: {
            url?: string
            fetchParams?: FetchParams
            body?: FetchOptions['body']
        }) =>
            ofetchInstance<T>(createUrlAppend(options), {
                method: HttpMethod.POST,
                body: options.body,
                onRequest: ({ options }: FetchContext) => {
                    options.headers = {
                        ...options.headers,
                        ...contentTypeHeaders(),
                        ...authHeader(),
                        ...switchHeader()
                    }
                }
            }),
        put: <T>(options: {
            url?: string
            fetchParams?: FetchParams
            body?: FetchOptions['body']
        }) =>
            ofetchInstance<T>(createUrlAppend(options), {
                method: HttpMethod.PUT,
                body: options.body,
                onRequest: ({ options }: FetchContext) => {
                    options.headers = {
                        ...options.headers,
                        ...contentTypeHeaders(),
                        ...authHeader(),
                        ...switchHeader()
                    }
                }
            }),
        patch: <T>(options: {
            url?: string
            fetchParams?: FetchParams
            body?: FetchOptions['body']
        }) =>
            ofetchInstance<T>(createUrlAppend(options), {
                method: HttpMethod.PATCH,
                body: options.body,
                onRequest: ({ options }: FetchContext) => {
                    options.headers = {
                        ...options.headers,
                        ...contentTypeHeaders(false, true),
                        ...authHeader(),
                        ...switchHeader()
                    }
                }
            }),
        delete: <T>(options: { url?: string; fetchParams?: FetchParams }) =>
            ofetchInstance<T>(createUrlAppend(options), {
                method: HttpMethod.DELETE,
                onRequest: ({ options }: FetchContext) => {
                    options.headers = {
                        ...options.headers,
                        ...contentTypeHeaders(),
                        ...authHeader(),
                        ...switchHeader()
                    }
                }
            })
    }
}

const contentTypeHeaders = (
    isCsv: boolean = false,
    isPatch: boolean = false
): Record<string, string> => {
    return !isCsv
        ? {
              Accept: 'application/ld+json',
              'Content-Type': isPatch ? 'application/merge-patch+json' : 'application/ld+json'
          }
        : {
              Accept: 'text/csv',
              responseType: 'blob'
          }
}

const authHeader = (): Record<string, string> => {
    const { token } = useAuthStore()
    const isLoggedIn = !!token

    if (isLoggedIn) {
        return { Authorization: `Bearer ${token}` }
    }

    return {}
}

const switchHeader = (): Record<string, string> => {
    const { isUserSwitched, currentUser } = useAuthStore()

    if (isUserSwitched && currentUser) {
        return { '-SWITCH-USER': `${(currentUser as any).email?.email}` }
    }

    return {}
}
